import {Col, Form, Row} from 'react-bootstrap';
import {Ticket} from '../../../../../model/Ticket';
import {renderFormProps} from '../../../../FormController';
import React, {useEffect} from 'react';
import {ControlImportActions, DossierActions,} from '../../../../../components/app/App/DossierActions';
import {formatDate} from '../../../../../utils';
import {TransportationModeConstants} from '../../../../../constants/model';
import TextInput from '../../../../fields/TextInput';
import {ControlSwitchInput} from "../../../../../components/SwitchButton";
import ConditionalInput from "../../../../fields/ConditionalInput/ConditionalInput";
import SelectInput, {SelectInputOptions} from "../../../../fields/SelectInput";
import {Shipment} from "../../../../../model/Shipment";
import NumberInput from "../../../../fields/NumberInput";

interface MainTransportProps extends renderFormProps<Ticket> {
    shipment: Shipment,
}

const dateOption : Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
};

const currencyChoices = [
    {
        label: 'CAD',
        value: '8'
    },
    {
        label: 'CNY',
        value: '12'
    },
    {
        label: 'EUR',
        value: '19'
    },
    {
        label: 'USD',
        value: '61'
    },
    {
        label: 'ZAR',
        value: '65'
    },
]


const getMightContainADateLabel = (label: string, shipmentAttribute: Date | null, alternativeLabel: string, withTime = true) => {
    if (shipmentAttribute) {
        const text = `${label} ${formatDate(new Date(shipmentAttribute), 'fr-FR', dateOption, 'date')}`;
        return text.concat(
            ' ',
            `à ${formatDate(new Date(shipmentAttribute), 'fr-FR', dateOption, 'time')}`
                .repeat(Number(withTime))
        );
    }
    return alternativeLabel;
}

export const MainTransport = ({
    register,
    resource,
    control,
    shipment,
    getValues,
    setValue
}: MainTransportProps) => {
    const actions: ControlImportActions[] = [];

    const customeo = typeof shipment.customeos[0] === 'string' ? null : shipment.customeos[0]

    useEffect(() => {
        const baeVal = getValues('bae')
        setValue('bae', !!(customeo && customeo.baeClearedDate) || baeVal);

        const baeEcsVal = getValues('baeEcs')
        setValue('baeEcs', !!(customeo && customeo.baeEcsClearedDate) || baeEcsVal);

    }, [customeo, setValue, getValues]);

    actions.push(
        {
            label: 'Demande booking cie ?',
            name: 'askedCompanyBooking',
            value: resource.askedCompanyBooking,
        },
        {
            label: 'Confirmation booking cie ?',
            name: 'confirmedCompanyBooking',
            value: resource.confirmedCompanyBooking,
        },
        {
            label: 'Confirmation booking client ?',
            name: 'confirmedCustomerBooking',
            value: resource.confirmedCustomerBooking,
        },
    )

    if (shipment.transportationMode === TransportationModeConstants.SEA) {
        actions.push(
            {
                label: 'MAD ?',
                name: 'mad',
                value: resource.mad,
            },
            {
                label: 'Création DOSS CCS ?',
                name: 'dossCcsCreation',
                value: resource.dossCcsCreationDate ? true : resource.dossCcsCreation,
                switchOnChildren: [
                    {
                        label: getMightContainADateLabel('', resource.dossCcsFFCreationDate, 'Date CCS'),
                        name: 'dossCcsCreationDate',
                        value: resource.dossCcsCreationDate,
                        type: resource.dossCcsFFCreationDate ? 'label' : 'datetime-local',
                    },
                ],
                type: resource.dossCcsCreationDate ? 'label' : 'bool'
            },
        )
    }

    actions.push(
        {
            label: 'Réception docs client ?',
            name: 'customerDocsReceived',
            value: resource.customerDocsReceived,
            switchOnChildren: [
                {
                    label: 'Reçu le ?',
                    name: 'customerDocsReceivedDate',
                    value: resource.customerDocsReceivedDate,
                    type: 'datetime-local',
                },
            ],
            type: resource.customerDocsReceivedDate ? 'label' : 'bool'
        },
    )

    if (shipment.transportationMode === TransportationModeConstants.SEA) {
        actions.push(
            {
                label: 'VGM ?',
                name: 'vgm',
                value: resource.vgm,
                switchOnChildren: [
                    {
                        label: 'Date VGM',
                        name: 'vgmDate',
                        value: resource.vgmDate,
                        type: 'datetime-local',
                    },
                ],
            },
        )
    }

    actions.push(
        {
            label: 'Lancer le “Ok for clearance” de MyCustoms ?', // readonly sur le fichier de correspondance
            name: 'isTriggerMyCustomsOkForClearance',
            value: false,
            type: 'label',
            switchOffChildren: [
                {
                    label: customeo?.clearance ? 'Oui' : 'Non',
                    type: 'label',
                    name: 'triggerMyCustomsOkForClearance'
                }
            ]
        },
        {
            label: 'BAE ?',
            name: 'bae',
            switchOnChildren: [
                {
                    label: getMightContainADateLabel('', customeo?.baeClearedDate ?? null, 'Date BAE'),
                    name: 'baeDate',
                    type: customeo?.baeClearedDate ? 'label' : 'datetime-local',
                },
            ],
            type: customeo?.baeClearedDate || resource.baeDate ? 'label' : 'bool'
        },
        {
            label: 'EUR1+ATR ?',
            name: 'eur1AndAtr',
            value: resource.eur1AndAtr,
        },
        {
            label: 'BAE-ECS ?',
            name: 'baeEcs',
            value: resource.baeEcs,
            switchOnChildren: [
                {
                    label: getMightContainADateLabel('', customeo?.baeEcsClearedDate ?? null, 'Date ECS'),
                    name: 'baeEcsClearedDate',
                    type: customeo?.baeEcsClearedDate ? 'label' : 'datetime-local',
                },
            ],
            type: customeo?.baeEcsClearedDate || resource.baeEcsClearedDate ? 'label' : 'bool'
        },
        {
            label: 'BASE ?',
            name: 'bas',
            value: resource.bas,
            switchOnChildren: [
                {
                    label: getMightContainADateLabel('', shipment.basiDate, 'Date BASE'),
                    name: 'basDate',
                    type: shipment.basiDate ? 'label' : 'datetime-local',
                    value: resource.basDate
                },
            ],
            type: shipment.basiDate || resource.basDate ? 'label' : 'bool'
        },
        {
            label: 'Instructions BL cie ?',
            name: 'blCompanyInstruction',
            value: resource.blCompanyInstruction,
        },
        {
            label: 'Réception du BL ?',
            name: 'blReceived',
            value: resource.blReceived,
        },
        {
            label: 'HBL ?',
            name: 'hbl',
            value: resource.hbl,
        },
        {
            label: 'Draft client ?',
            name: 'customerDraft',
            value: resource.customerDraft,
        },
    )

    const postHblAction: ControlImportActions[] = [];

    if (shipment.transportationMode === TransportationModeConstants.AIR) {
        postHblAction.push(
            {
                label: 'POUCH ?',
                name: 'pouch',
                value: resource.pouch,
            },
        )
    }

    if (shipment.transportationMode === TransportationModeConstants.SEA) {
        postHblAction.push(
            {
                label: 'TIMAD ?',
                name: 'timad',
                value: resource.timad,
            },
            {
                label: 'ISF ?',
                name: 'isf',
                value: resource.isf,
            },
            {
                label: 'AMS ?',
                name: 'ams',
                value: resource.ams,
            },
            {
                label: 'BESC ?',
                name: 'besc',
                value: resource.besc,
            },
        )
    }

    postHblAction.push(
        {
            label: 'VAB ?',
            name: 'vab',
            value: resource.vabDate ? true : resource.vab,
            switchOnChildren: [
                {
                    label: 'Date VAB',
                    name: 'vabDate',
                    value: resource.vabDate,
                    type: 'datetime-local',
                },
            ],
            type: resource.vabDate ? 'label' : 'bool'
        },
        {
            label: 'Confirmation emb. cie ?',
            name: 'boardingCompanyConfirmation',
            value: resource.boardingCompanyConfirmation,
        },
        {
            label: 'Confirmation emb. client ?',
            name: 'boardingCustomerConfirmation',
            value: resource.boardingCustomerConfirmation,
        },
        {
            label: 'Pré-alerte ?',
            name: 'preWarning',
            value: resource.preWarning,
        },
    )

    const getTrafficModeOptions = () => {
        const options: SelectInputOptions[] = [];
        Object.entries(TransportationModeConstants).forEach(([,value]) => {
            switch (value) {
                case TransportationModeConstants.ROAD:
                    options.push({label: 'Routier', value: value})
                    break;
                case TransportationModeConstants.SEA:
                    options.push({label: 'Maritime', value: value})
                    break;
                case TransportationModeConstants.AIR:
                    options.push({label: 'Aérien', value: value})
                    break;
                default:
                    options.push({label: 'Ferroviaire', value: value})
                    break;
            }
        })
        return options;
    }

    return (
        <div className={'border-top ms-0 me-0 pt-3 pb-5'}>
            <Row>
                <Col lg={8}>
                    <h2>Transport Principal</h2>
                    <div className={'d-flex flex-column h-100 justify-content-between pb-5'}>
                        <div>
                            <Row>
                                <Col lg={4}>
                                    <SelectInput
                                        label={'Mode de transport principal'}
                                        name={'shipment.transportationMode'}
                                        singleValueClasses={'text-primary'}
                                        control={control}
                                        selectOptions={getTrafficModeOptions()}
                                    />
                                </Col>
                                <Col lg={4}>
                                    {/* readonly sur fichier correspondance */}
                                    <p className={'label'}>
                                        N° de vol, train, Nom du navire, Camion
                                    </p>
                                    <p>
                                        {shipment.trafficIdentification ?? '-'}
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    {/* readonly sur fichier correspondance */}
                                    <p className={'label'}>
                                        N° de VOY
                                    </p>
                                    <p>
                                        {shipment.travelNumber ?? '-'}
                                    </p>
                                </Col>
                            </Row>
                            <Row>
                                <Col lg={4}>
                                    {/* readonly sur fichier correspondance */}
                                    <p className={'label'}>
                                        Lieu de chargement
                                    </p>
                                    <p>
                                        {shipment.loadingPlatform?.name ?? '-'} ({shipment.loadingPlatform?.country ?? '-'})
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    {/* readonly sur fichier correspondance */}
                                    <p className={'label'}>
                                        ETD
                                    </p>
                                    <p>
                                        {shipment.ETD ?
                                            formatDate(shipment.ETD, 'fr-FR', {
                                                year: "numeric",
                                                month: "2-digit",
                                                day: "2-digit"
                                            }) : '-'
                                        }
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    {/* readonly sur fichier correspondance */}
                                    <p className={'label'}>
                                        ATD
                                    </p>
                                    <p>
                                        {shipment.ATD ?
                                            formatDate(shipment.ATD, 'fr-FR', {
                                                year: "numeric",
                                                month: "2-digit",
                                                day: "2-digit"
                                            }) : '-'
                                        }
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    {/* readonly sur fichier correspondance */}
                                    <p className={'label'}>
                                        Lieu de déchargement
                                    </p>
                                    <p>
                                        {shipment.unloadingPlatform?.name ?? '-'} ({shipment.unloadingPlatform?.country ?? '-'})
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <Row>
                                        <Col>
                                            {/* readonly sur fichier correspondance */}
                                            <p className={'label'}>
                                                ETA
                                            </p>
                                            <p className={'text-primary'}>
                                                {shipment.ETA ?
                                                    formatDate(shipment.ETA, 'fr-FR', {
                                                        year: "numeric",
                                                        month: "2-digit",
                                                        day: "2-digit"
                                                    }) : '-'
                                                }
                                            </p>
                                        </Col>
                                        {
                                            shipment.PRD ?
                                                <Col className={'text-success'}>
                                                    <p className={'label'}>
                                                        PRD
                                                    </p>
                                                    <p>
                                                        {formatDate(shipment.PRD, 'fr-FR', {
                                                            year: "numeric",
                                                            month: "2-digit",
                                                            day: "2-digit"
                                                        })}
                                                    </p>
                                                </Col>
                                                : null
                                        }
                                    </Row>
                                </Col>
                                <Col lg={4}>
                                    {/* readonly sur fichier correspondance */}
                                    <p className={'label'}>
                                        ATA
                                    </p>
                                    <p>
                                        {shipment.ATA ?
                                            formatDate(shipment.ATA, 'fr-FR', {
                                                year: "numeric",
                                                month: "2-digit",
                                                day: "2-digit"
                                            }) : '-'
                                        }
                                    </p>
                                </Col>
                            </Row>
                            <Row>
                                <Col lg={4}>
                                    {/* readonly sur fichier correspondance */}
                                    <p className={'label'}>
                                        Compagnie
                                    </p>
                                    <p>
                                        {shipment.imposedCompany?.name ?? '-'}
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    {/* readonly sur fichier correspondance */}
                                    <p className={'label'}>
                                        LTA / CMR / BL
                                    </p>
                                    <p>
                                        {shipment.blLtaCmr ?? '-' }
                                    </p>
                                </Col>
                            </Row>
                            <Row>
                                <Col lg={4}>
                                    {/* readonly sur fichier correspondance */}
                                    <p className={'label'}>
                                        Numéro de booking
                                    </p>
                                    <p>
                                        {shipment.bookingNumber ?? '-'}
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <TextInput
                                        name={'shipment.filingNumber'}
                                        label={'Numéro de filing'}
                                        control={control}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col lg={4}>
                                    <p className={'label'}>
                                        Terminal / Entrepôt / Magasin
                                    </p>
                                    <p>
                                        {shipment.warehouse ?? '-'}
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <TextInput
                                        name={'shipment.transportFeNumber'}
                                        label={'Numéro de FE'}
                                        control={control}
                                    />
                                </Col>
                                <Col lg={4}>
                                    <TextInput
                                        name={'shipment.transportIcvNumber'}
                                        label={'Numéro BBE'}
                                        control={control}
                                    />

                                </Col>
                            </Row>
                            <Row>
                                <Col lg={4}>
                                    <p className={'label'}>
                                        Destination finale
                                    </p>
                                    <p>
                                        {shipment.destinationPlatform ? shipment.destinationPlatform.name : '-'}
                                    </p>
                                </Col>
                                <Col lg={4}>
                                    <p className={'label'}>
                                        Prévue le
                                    </p>
                                    <p>
                                        {shipment.destinationPredictiveDate ?
                                            formatDate(shipment.destinationPredictiveDate, 'fr-FR', {
                                                year: "numeric",
                                                month: "2-digit",
                                                day: "2-digit"
                                            }) : '-'
                                        }
                                    </p>
                                </Col>
                            </Row>
                            <Row>
                                <Col lg={2}>
                                    <p className={'label'}>
                                        Clôture Douane
                                    </p>
                                    <p>
                                        {shipment.mainClosingDate ?
                                            formatDate(shipment.mainClosingDate, 'fr-FR', {
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            }) : '-'
                                        }
                                    </p>
                                </Col>
                                <Col lg={2}>
                                    <p className={'label'}>
                                        Clôture Doc
                                    </p>
                                    <p>
                                        {shipment.docClosingDate ?
                                            formatDate(shipment.docClosingDate, 'fr-FR', {
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            }) : '-'
                                        }
                                    </p>
                                </Col>
                                <Col lg={2}>
                                    <p className={'label'}>
                                        Clôture VGM
                                    </p>
                                    <p>
                                        {shipment.vgmClosingDate ?
                                            formatDate(shipment.vgmClosingDate, 'fr-FR', {
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            }) : '-'
                                        }
                                    </p>
                                </Col>
                                <Col lg={2}>
                                    <p className={'label'}>
                                        Clôture DGX
                                    </p>
                                    <p>
                                        {shipment.dgxClosingDate ?
                                            formatDate(shipment.dgxClosingDate, 'fr-FR', {
                                                year: 'numeric',
                                                month: '2-digit',
                                                day: '2-digit',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                            }) : '-'
                                        }
                                    </p>
                                </Col>
                            </Row>
                        </div>
                    </div>
                </Col>
                <Col lg={4}>
                    {
                        DossierActions(
                            actions,
                            register,
                            control,
                        )
                    }
                    {
                        DossierActions(
                            postHblAction,
                            register,
                            control,
                        )
                    }
                </Col>
            </Row>

            <Row>
                <Col lg={8}>
                    <h4>Frais au départ</h4>
                    <Row>
                        <Col lg={4}>
                            <p className={'label'}>
                                Inland cost
                            </p>
                            <Row>
                                {
                                    customeo
                                        ? (
                                            <>
                                                <Col>
                                                    {
                                                        customeo.inlandCost
                                                            ? customeo.inlandCost  + ' ' + customeo.inlandCurrency?.shortName ?? ''
                                                            : '-'
                                                    }
                                                </Col>
                                            </>
                                        ) : (
                                            <>
                                                <Col lg={7}>
                                                    <NumberInput
                                                        name={'customeoInlandCost'}
                                                        control={control}
                                                    />
                                                </Col>
                                                <Col lg={5}>
                                                    <SelectInput
                                                        name={'customeoInlandCostCurrency'}
                                                        control={control}
                                                        selectOptions={currencyChoices}
                                                        isClearable={false}
                                                    />
                                                </Col>
                                            </>
                                        )
                                }
                            </Row>
                        </Col>
                        <Col lg={4}>
                            <p className={'label'}>
                                Freight charges
                            </p>
                            <Row>
                                {
                                    customeo
                                        ? (
                                            <>
                                                <Col>
                                                    {
                                                        customeo.freightCharges
                                                            ? customeo.freightCharges  + ' ' + customeo.freightChargesCurrency?.shortName ?? ''
                                                            : '-'
                                                    }
                                                </Col>
                                            </>
                                        ) : (
                                            <>
                                                <Col lg={7}>
                                                    <NumberInput
                                                        name={'customeoFreightCharges'}
                                                        control={control}
                                                    />
                                                </Col>
                                                <Col lg={5}>
                                                    <SelectInput
                                                        name={'customeoFreightChargesCurrency'}
                                                        control={control}
                                                        selectOptions={currencyChoices}
                                                        isClearable={false}
                                                    />
                                                </Col>
                                            </>
                                        )
                                }
                            </Row>
                        </Col>
                        <Col lg={4}>
                            <p className={'label'}>
                                Insurance amount
                            </p>
                            <Row>
                                {
                                    customeo
                                        ? (
                                            <>
                                                <Col>
                                                    {
                                                        customeo.insuranceAmount
                                                            ? customeo.insuranceAmount  + ' ' + customeo.insuranceAmountCurrency?.shortName ?? ''
                                                            : '-'
                                                    }
                                                </Col>
                                            </>
                                        ) : (
                                            <>
                                                <Col lg={7}>
                                                    <NumberInput
                                                        name={'customeoInsuranceAmount'}
                                                        control={control}
                                                    />
                                                </Col>
                                                <Col lg={5}>
                                                    <SelectInput
                                                        name={'customeoInsuranceAmountCurrency'}
                                                        control={control}
                                                        selectOptions={currencyChoices}
                                                        isClearable={false}
                                                    />
                                                </Col>
                                            </>
                                        )
                                }
                            </Row>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={12}>
                            <ControlSwitchInput
                                label={'Emettre des réserves ?'}
                                control={control}
                                name={'isReserved'}
                                value={resource.isReserved}
                            />
                            <ConditionalInput
                                control={control}
                                fieldName={'isReserved'}
                                renderField={
                                    () => <>
                                        <Form.Control
                                            as="textarea"
                                            rows={4}
                                            {...register('reserved')}
                                            defaultValue={resource.reserved ?? ''}
                                        />
                                    </>
                                }
                                fieldValue={true}
                                defaultValue={!!resource.reserved}
                            />
                        </Col>
                    </Row>
                </Col>
            </Row>
        </div>
    );
};
